import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import placeholderProfile from '../assets/placeholder-profile.png';
import './ProfileAvatarViewer.css';

function ProfileAvatarViewer({
  profilePic,
  name,
  onClose,
  onCreateAvatar,
}) {
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const [imageFailed, setImageFailed] = useState(false);
  const [fallbackFailed, setFallbackFailed] = useState(false);

  onCloseRef.current = onClose;

  useEffect(() => {
    setImageFailed(false);
    setFallbackFailed(false);
  }, [profilePic]);

  useEffect(() => {
    const previousFocus = document.activeElement;
    const root = document.getElementById('root');
    const previousInert = root?.inert;
    const previousOverflow = document.body.style.overflow;

    // The portal sits outside the app root.
    if (root) root.inert = true;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onCloseRef.current?.();
        return;
      }

      if (event.key !== 'Tab') return;

      const buttons = Array.from(
        dialogRef.current?.querySelectorAll(
          'button:not([disabled])'
        ) || []
      );

      const first = buttons[0];
      const last = buttons[buttons.length - 1];

      if (!first) return;

      const focusInside = dialogRef.current?.contains(
        document.activeElement
      );

      if (
        event.shiftKey &&
        (!focusInside || document.activeElement === first)
      ) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        (!focusInside || document.activeElement === last)
      ) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.body.style.overflow = previousOverflow;

      if (root) root.inert = previousInert;

      if (previousFocus?.isConnected) {
        previousFocus.focus?.({ preventScroll: true });
      }
    };
  }, []);

  const usingFallback = !profilePic || imageFailed;
  const imageSrc = usingFallback
    ? placeholderProfile
    : profilePic;

  return createPortal(
    <div
      className="profile-avatar-viewer"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose?.();
        }
      }}
    >
      <div
        ref={dialogRef}
        className="profile-avatar-viewer__dialog"
        role="dialog"
        aria-modal="true"
        aria-label={`${name || 'User'} profile picture`}
      >
        <header className="profile-avatar-viewer__header">
          <span className="profile-avatar-viewer__name">
            {name || 'Profile picture'}
          </span>

          <button
            ref={closeRef}
            type="button"
            className="profile-avatar-viewer__close"
            onClick={onClose}
            aria-label="Close profile picture"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="m6 6 12 12M18 6 6 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>

        <div className="profile-avatar-viewer__image-wrap">
          {fallbackFailed ? (
            <p role="status">Profile picture unavailable</p>
          ) : (
            <img
              key={imageSrc}
              src={imageSrc}
              alt={`${name || 'User'} profile picture`}
              className="profile-avatar-viewer__image"
              draggable={false}
              onError={() => {
                if (usingFallback) {
                  setFallbackFailed(true);
                } else {
                  setImageFailed(true);
                }
              }}
            />
          )}
        </div>

        {typeof onCreateAvatar === 'function' && (
          <footer className="profile-avatar-viewer__footer">
            <button
              type="button"
              className="profile-avatar-viewer__create"
              onClick={onCreateAvatar}
            >
              Create my avatar
            </button>
          </footer>
        )}
      </div>
    </div>,
    document.body
  );
}

export default ProfileAvatarViewer;
